import React, { useState, useEffect, useRef } from 'react';
import { validateStep1 } from '../../utils/helpers';
import './StepFormWidget.css';

/* ── Confetti helper ── */
const launchConfetti = (canvas) => {
  const ctx = canvas.getContext('2d');
  canvas.width  = canvas.offsetWidth;
  canvas.height = canvas.offsetHeight;
  const COLORS = ['#1a56db','#0ea5e9','#22c55e','#f59e0b','#ec4899','#a855f7','#f97316'];
  const COUNT  = 120;
  const particles = Array.from({ length: COUNT }, () => {
    const size = Math.random() * 9 + 5;
    return {
      x: canvas.width * Math.random(), y: canvas.height * Math.random() - canvas.height,
      w: size, h: size * (Math.random() > 0.5 ? 0.4 : 1),
      rot: Math.random() * 360, rotV: (Math.random() - 0.5) * 6,
      vx: (Math.random() - 0.5) * 3, vy: Math.random() * 3.5 + 2,
      color: COLORS[Math.floor(Math.random() * COLORS.length)], alpha: 1,
    };
  });
  let raf;
  const draw = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let alive = false;
    particles.forEach(p => {
      if (p.y > canvas.height + 20) return;
      alive = true;
      p.x += p.vx; p.y += p.vy; p.rot += p.rotV;
      if (p.y > canvas.height * 0.6) p.alpha = Math.max(0, p.alpha - 0.018);
      ctx.save(); ctx.globalAlpha = p.alpha;
      ctx.translate(p.x, p.y); ctx.rotate((p.rot * Math.PI) / 180);
      ctx.fillStyle = p.color; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    });
    if (alive) raf = requestAnimationFrame(draw);
    else ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
  draw();
  return () => cancelAnimationFrame(raf);
};

const PHP_API_URL = 'https://mbbsabroad.mycollegebuddy.com/submit_enquiry.php';

/* ══════════════════════════════════════════════
   🛡️  SECURITY HELPERS
══════════════════════════════════════════════ */

// Patterns that indicate malicious/invalid input
const MALICIOUS_PATTERNS = [
  /<\s*script[\s\S]*?>/i,           // <script>
  /<\s*\/\s*script>/i,              // </script>
  /javascript\s*:/i,                // javascript:
  /on\w+\s*=\s*["'`]?[^"'`>]*/i,  // onclick=, onerror=, etc.
  /<\s*(iframe|object|embed|link|meta|base|form)[^>]*>/i, // dangerous tags
  /data\s*:\s*text\/html/i,         // data:text/html
  /vbscript\s*:/i,                  // vbscript:
  /expression\s*\(/i,               // CSS expression()
  /eval\s*\(/i,                     // eval(
  /document\.(cookie|write|location)/i, // DOM attacks
  /window\.(location|open)/i,       // redirect attacks
  /(union|select|insert|drop|delete|update|exec)\s+/i, // SQL injection
  /--\s*$/,                         // SQL comment
  /[<>'"`;]/,                       // raw HTML/SQL special chars
];

// URL / link pattern — disallow pasting links in name/phone/city/message
const URL_PATTERN = /((https?|ftp):\/\/|www\.)\S+/i;

// Field-specific rules
const FIELD_RULES = {
  name:    { pattern: /^[a-zA-Z\s.'-]{0,60}$/, msg: 'Name can only contain letters, spaces, or . \' -' },
  phone:   { pattern: /^[6-9]?\d{0,9}$/, msg: 'Phone must start with 6–9 and contain only digits' },
  city:    { pattern: /^[a-zA-Z\s,.-]{0,50}$/, msg: 'City name contains invalid characters' },
  message: { pattern: /^[\s\S]{0,500}$/, msg: 'Message too long (max 500 characters)' },
};

/**
 * Returns { isMalicious: bool, reason: string }
 */
const checkMalicious = (value) => {
  for (const regex of MALICIOUS_PATTERNS) {
    if (regex.test(value)) return { isMalicious: true, reason: '🚨 Malicious input detected and blocked!' };
  }
  if (URL_PATTERN.test(value)) return { isMalicious: true, reason: '🔗 Links are not allowed in this field.' };
  return { isMalicious: false, reason: '' };
};

/* ══════════════════════════════════════════════ */

const StepFormWidget = ({ onClose }) => {
  const [curStep, setCurStep]         = useState(1);
  const [formData, setFormData]       = useState({
    name: '', email: '', phone: '', city: '', destination: 'india',
    country: '', neet: 'yes', score: '', budget: '', message: ''
  });
  const [errors, setErrors]           = useState({});
  const [securityAlert, setSecurityAlert] = useState('');
  const [submitted, setSubmitted]     = useState(false);
  const [isLoading, setIsLoading]     = useState(false);
  const [apiError, setApiError]       = useState('');
  const [enquiryId, setEnquiryId]     = useState(null);
  const [urlCome]                     = useState(() => window.location.href);
  const confettiRef                   = useRef(null);
  const alertTimerRef                 = useRef(null);

  /* ── Show security alert for 4 seconds ── */
  const showSecurityAlert = (msg) => {
    setSecurityAlert(msg);
    clearTimeout(alertTimerRef.current);
    alertTimerRef.current = setTimeout(() => setSecurityAlert(''), 4000);
    shake();
  };

  /* ── Enhanced handleInputChange with real-time security check ── */
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const key = id.replace('v_', '');

    // 1️⃣ Block malicious content immediately (skip for email — it has special chars like @ . + -)
    if (key !== 'email') {
      const { isMalicious, reason } = checkMalicious(value);
      if (isMalicious) {
        showSecurityAlert(reason);
        return;
      }
    }
    // 2️⃣ Phone — digits only, max 10, must start with 6-9
    if (key === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      // If user typed a non-6-9 first digit, warn immediately
      if (digitsOnly.length === 1 && !/^[6-9]/.test(digitsOnly)) {
        setErrors(prev => ({ ...prev, phone: 'Phone must start with 6, 7, 8, or 9' }));
      } else {
        setErrors(prev => ({ ...prev, phone: '' }));
      }
      setFormData(prev => ({ ...prev, phone: digitsOnly }));
      return;
    }

    // 3️⃣ Field-specific pattern validation (real-time)
    if (FIELD_RULES[key] && value !== '') {
      if (!FIELD_RULES[key].pattern.test(value)) {
        setErrors(prev => ({ ...prev, [key]: FIELD_RULES[key].msg }));
        return; // reject character that broke the rule
      }
    }

    // 4️⃣ Clear error if it was previously set and now valid
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const isValidPhone = (phone) => /^[6-9]\d{9}$/.test(phone);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const pickOpt = (group, val) => {
    setFormData(prev => ({ ...prev, [group]: val }));
    if (errors[group]) setErrors(prev => ({ ...prev, [group]: '' }));
  };

  const pickBudget = (val) => {
    setFormData(prev => ({ ...prev, budget: val }));
    if (errors.budget) setErrors(prev => ({ ...prev, budget: '' }));
  };

  const shake = () => {
    const el = document.querySelector('.sfw-card');
    if (!el) return;
    el.style.animation = 'shakeX 0.4s';
    setTimeout(() => { el.style.animation = ''; }, 500);
  };

  // 🆕 Function to push generate_lead event to GTM
  const pushGenerateLeadEvent = (enquiryIdData, formDataSubmitted) => {
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        'event': 'generate_lead',
        'enquiry_id': enquiryIdData,
        'lead_name': formDataSubmitted.name,
        'lead_email': formDataSubmitted.email,
        'lead_phone': formDataSubmitted.phone,
        'lead_destination': formDataSubmitted.destination,
        'lead_budget': formDataSubmitted.budget,
        'lead_neet_score': formDataSubmitted.score,
        'timestamp': new Date().toISOString()
      });
      console.log('GTM generate_lead event pushed');
    } else {
      console.warn('dataLayer not available');
    }
  };

  const callAPI = async (payload) => {
    const res = await fetch(PHP_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
    let result;
    try { result = await res.json(); }
    catch { throw new Error(`Server returned non-JSON response (HTTP ${res.status}).`); }
    if (!res.ok || !result.success) throw new Error(result.message || `Server error (HTTP ${res.status})`);
    return result;
  };

  const goNext = async () => {
    let ve = {};
    if (curStep === 1) {
      if (!formData.name.trim()) ve.name = 'Full name is required';
      else if (!/^[a-zA-Z\s.'-]{2,60}$/.test(formData.name.trim())) ve.name = 'Enter a valid full name';

      if (!formData.email.trim()) ve.email = 'Email is required';
      else if (!isValidEmail(formData.email)) ve.email = 'Enter a valid email address';

      if (!formData.phone) ve.phone = 'Phone number is required';
      else if (!isValidPhone(formData.phone)) ve.phone = 'Enter a valid 10-digit mobile number (starts with 6–9)';
    }

    if (Object.keys(ve).length > 0) { setErrors(ve); shake(); return; }

    if (curStep === 1) {
      setIsLoading(true);
      try {
        if (enquiryId) {
          await callAPI({ action: 'step1_update', enquiry_id: enquiryId, name: formData.name, email: formData.email, phone: formData.phone });
        } else {
          const result = await callAPI({ action: 'step1', name: formData.name, email: formData.email, phone: formData.phone, url_come: urlCome });
          setEnquiryId(result.enquiry_id);
        }
      } catch (err) { console.warn('Step 1 save failed:', err.message); }
      finally { setIsLoading(false); }
    }
    setCurStep(prev => prev + 1);
  };

  const goPrev = () => setCurStep(prev => Math.max(1, prev - 1));

  const submitForm = async () => {
    setIsLoading(true); setApiError('');
    try {
      const result = await callAPI({ action: 'submit', enquiry_id: enquiryId, ...formData });
      console.log('Submitted, id:', result.enquiry_id);
      
      // 🆕 Push generate_lead event to GTM after successful submission
      pushGenerateLeadEvent(result.enquiry_id, formData);
      
      setSubmitted(true);
    } catch (err) { setApiError(err.message || 'Could not connect. Please try again.'); shake(); }
    finally { setIsLoading(false); }
  };

  const resetForm = (e) => {
    if (e) e.preventDefault();
    setCurStep(1); setSubmitted(false); setApiError(''); setEnquiryId(null); setSecurityAlert('');
    setFormData({ name: '', email: '', phone: '', city: '', destination: 'india', country: '', neet: 'yes', score: '', budget: '', message: '' });
    setErrors({});
  };

  // Cleanup alert timer on unmount
  useEffect(() => () => clearTimeout(alertTimerRef.current), []);

  useEffect(() => {
    if (!submitted || !confettiRef.current) return;
    const stop = launchConfetti(confettiRef.current);
    return stop;
  }, [submitted]);

  const steps = [
    { num: 1, icon: '👤', name: 'You' },
    { num: 2, icon: '📍', name: 'Location' },
    { num: 3, icon: '📋', name: 'Exam Details' },
    { num: 4, icon: '💰', name: 'Budget' },
  ];

  const stepTitles = [
    { icon: '👤', title: 'Personal Information', sub: "We'll send your personalised roadmap to these details." },
    { icon: '📍', title: 'Location & Preference', sub: 'Optional — helps us match you better. Skip anytime.' },
    { icon: '📋', title: 'NEET Mock Test Results', sub: '' },
    { icon: '💰', title: 'Budget & Final Details', sub: 'Optional — select your yearly investment range if known.' },
  ];

  return (
    <div className="sfw-card">

      {/* Security Alert Banner — shows instantly on malicious input */}
      {securityAlert && (
        <div className="sfw-security-alert">
          <span className="sfw-security-alert-icon">🛡️</span>
          {securityAlert}
          <button className="sfw-security-alert-close" onClick={() => setSecurityAlert('')}>✕</button>
        </div>
      )}

      {!submitted ? (
        <>
          <div className="sfw-card-header">
            <div className="sfw-header-top">
              <div className="sfw-header-badge">
                <span className="sfw-header-dot"></span>
                Free Counselling · No Obligation
              </div>
              <span className="sfw-header-secure">🔒 Secure</span>
            </div>
            <div className="sfw-header-title">Get Your Personalised MBBS Roadmap</div>
          </div>

          <div className="sfw-stepper">
            {steps.map(step => (
              <div key={step.num}
                className={`sfw-stepper-item ${curStep === step.num ? 'active' : ''} ${curStep > step.num ? 'done' : ''}`}>
                <div className="sfw-step-circle">
                  {curStep > step.num ? '✓' : step.num}
                </div>
                <span className="sfw-step-name">{step.name}</span>
              </div>
            ))}
          </div>

          <div className="sfw-prog">
            <div className="sfw-fill" style={{ width: `${(curStep / 4) * 100}%` }} />
          </div>

          <div className="sfw-body">

            {/* Step 1 */}
            <div className={`sfw-step ${curStep === 1 ? 'active' : ''}`}>
              <div className="sfw-step-title">{stepTitles[0].icon} {stepTitles[0].title}</div>
              <div className="sfw-step-sub">{stepTitles[0].sub}</div>
              <div className="sfw-group">
                <label className="sfw-label">Full Name <span>*</span></label>
                <input className={`sfw-input ${errors.name ? 'error' : ''}`} type="text" id="v_name"
                  value={formData.name} onChange={handleInputChange} placeholder="Enter your full name"
                  autoComplete="name" />
                {errors.name && <span className="sfw-err">{errors.name}</span>}
              </div>
              <div className="sfw-row">
                <div className="sfw-group">
                  <label className="sfw-label">Email <span>*</span></label>
                  <input className={`sfw-input ${errors.email ? 'error' : ''}`} type="email" id="v_email"
                    value={formData.email} onChange={handleInputChange} placeholder="you@example.com"
                    autoComplete="email" />
                  {errors.email && <span className="sfw-err">{errors.email}</span>}
                </div>
                <div className="sfw-group">
                  <label className="sfw-label">Phone <span>*</span></label>
                  <input className={`sfw-input ${errors.phone ? 'error' : ''}`} type="tel" id="v_phone"
                    value={formData.phone} onChange={handleInputChange} placeholder="10-digit number"
                    maxLength={10} inputMode="numeric" autoComplete="tel" />
                  {errors.phone
                    ? <span className="sfw-err">{errors.phone}</span>
                    : <span className="sfw-hint">{formData.phone.length}/10 digits</span>}
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className={`sfw-step ${curStep === 2 ? 'active' : ''}`}>
              <div className="sfw-step-title">{stepTitles[1].icon} {stepTitles[1].title}</div>
              <div className="sfw-step-sub">{stepTitles[1].sub}</div>
              <div className="sfw-group">
                <label className="sfw-label">Preferred Study Destination</label>
                <div className="sfw-opts">
                  {['india', 'abroad', 'both'].map(opt => (
                    <div key={opt} className={`sfw-opt ${formData.destination === opt ? 'sel' : ''}`}
                      onClick={() => pickOpt('destination', opt)}>
                      {opt === 'india' && '🇮🇳 India'}
                      {opt === 'abroad' && '🌍 Abroad'}
                      {opt === 'both' && '🔀 Both Options'}
                    </div>
                  ))}
                </div>
              </div>
              {(formData.destination === 'abroad' || formData.destination === 'both') && (
                <div className="sfw-group">
                  <label className="sfw-label">Preferred Country</label>
                  <div className="sfw-sel-wrap">
                    <select className="sfw-select" id="v_country" value={formData.country} onChange={handleInputChange}>
                      <option value="">Select a country</option>
                      <option>Russia</option><option>Georgia</option><option>Kazakhstan</option>
                      <option>Kyrgyzstan</option><option>Bangladesh</option><option>Europe</option>
                      <option>Uzbekistan</option><option>Other</option>
                    </select>
                  </div>
                </div>
              )}
              <div className="sfw-group">
                <label className="sfw-label">Your City</label>
                <input className={`sfw-input ${errors.city ? 'error' : ''}`} type="text" id="v_city"
                  value={formData.city} onChange={handleInputChange} placeholder="e.g. Mumbai, Delhi, Pune" />
                {errors.city && <span className="sfw-err">{errors.city}</span>}
              </div>
            </div>

            {/* Step 3 */}
            <div className={`sfw-step ${curStep === 3 ? 'active' : ''}`}>
              <div className="sfw-step-title">{stepTitles[2].icon} {stepTitles[2].title}</div>
              <div className="sfw-step-sub">{stepTitles[2].sub}</div>
              <div className="sfw-group">
                <label className="sfw-label">Have you attempted NEET Mock Test?</label>
                <div className="sfw-opts">
                  {[{ val: 'yes', label: '✅ Yes, I have' }, { val: 'no', label: '❌ Not yet' }, { val: 'upcoming', label: '📅 Appearing soon' }].map(opt => (
                    <div key={opt.val} className={`sfw-opt ${formData.neet === opt.val ? 'sel' : ''}`}
                      onClick={() => pickOpt('neet', opt.val)}>{opt.label}</div>
                  ))}
                </div>
              </div>
              {formData.neet !== 'no' && (
                <div className="sfw-group">
                  <label className="sfw-label">Your Mock Test Score range</label>
                  <div className="sfw-opts">
                    {[{ val: 'u200', label: 'Below 200' }, { val: '200-350', label: '200 – 350' },
                      { val: '350-500', label: '350 – 500' }, { val: '500-600', label: '500 – 600' },
                      { val: '600+', label: '600 – 720' }].map(opt => (
                      <div key={opt.val} className={`sfw-opt ${formData.score === opt.val ? 'sel' : ''}`}
                        onClick={() => pickOpt('score', opt.val)}>{opt.label}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Step 4 */}
            <div className={`sfw-step ${curStep === 4 ? 'active' : ''}`}>
              <div className="sfw-step-title">{stepTitles[3].icon} {stepTitles[3].title}</div>
              <div className="sfw-step-sub">{stepTitles[3].sub}</div>
              <div className="sfw-group">
                <label className="sfw-label">Total MBBS Budget (INR per year)</label>
                <div className="sfw-budget">
                  {[{ val: 'u15', icon: '🌱', label: 'Under ₹15L/yr', sub: 'Scholarship focus' },
                    { val: '15-25', icon: '💼', label: '₹15 – ₹25L/yr', sub: 'Budget-friendly' },
                    { val: '25-45', icon: '🏆', label: '₹25 – ₹45L/yr', sub: 'Mid-range options' },
                    { val: '45+', icon: '🚀', label: 'Above ₹45L/yr', sub: 'Premium colleges' }].map(opt => (
                    <div key={opt.val} className={`sfw-budopt ${formData.budget === opt.val ? 'sel' : ''}`}
                      onClick={() => pickBudget(opt.val)}>
                      <div className="sfw-bicon">{opt.icon}</div>
                      <div className="sfw-bval">{opt.label}</div>
                      <div className="sfw-bsub">{opt.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sfw-group">
                <label className="sfw-label">Additional Message (optional)</label>
                <textarea className="sfw-textarea" id="v_message" value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Share any specific goals, concerns, or questions..." />
                <span className="sfw-hint">{formData.message.length}/500 characters</span>
              </div>
            </div>

          </div>

          <div className="sfw-footer">
            <button className="sfw-btn-back" onClick={goPrev} disabled={isLoading}
              style={{ visibility: curStep === 1 ? 'hidden' : 'visible' }}>← Back</button>
            <div className="sfw-step-info">
              Step <b>{curStep}</b> / 4
              {curStep > 1 && <span className="sfw-optional-tag">Optional</span>}
            </div>
            <div className="sfw-footer-right">
              <button className="sfw-btn-next" onClick={curStep === 4 ? submitForm : goNext} disabled={isLoading}>
                {curStep === 4
                  ? (isLoading ? '⏳ Submitting...' : '🎯 Submit')
                  : (isLoading && curStep === 1 ? '⏳ Saving...' : 'Continue →')}
              </button>
            </div>
          </div>

          {apiError && <div className="sfw-api-err">⚠️ {apiError}</div>}
        </>
      ) : (
        <div className="sfw-success">
          <canvas ref={confettiRef} className="sfw-confetti-canvas" aria-hidden="true" />
          <div className="sfw-success-icon">🎉</div>
          <div className="sfw-suc-title">Application Received!</div>
          <p className="sfw-suc-sub">
            Our counsellor will reach out within <strong>24 hours</strong> with a
            personalised MBBS roadmap tailored to your profile.
          </p>
          <div className="sfw-suc-tags">
            <span className="sfw-suc-tag">✅ Application Saved</span>
            <span className="sfw-suc-tag">🔒 Data Secure</span>
          </div>
          {onClose && <button className="sfw-close-after" onClick={onClose}>Close</button>}
          <p style={{ marginTop: '1rem', fontSize: '0.78rem', color: 'var(--muted)' }}>
            <a href="#" onClick={resetForm} style={{ color: 'var(--primary)' }}>Submit another application</a>
          </p>
        </div>
      )}
    </div>
  );
};

export default StepFormWidget;